// renderers/pdf/PdfBuilder.js
export default class PdfBuilder {
  constructor(doc, locale) {
    this.doc = doc;
    this.locale = locale || "de";
    this.margin = 10;

    this.pageWidth = doc.internal.pageSize.width;
    this.pageHeight = doc.internal.pageSize.height;

    this.headerHeight = 15;
    this.lineHeight = 7;

    this.y = this.margin + this.headerHeight;

    // Colors consistent with your old implementation
    this.primary = "#003E81";
    this.accent = "#4B5EAA";
    this.text = "#000000";
    this.white = "#FFFFFF";

    // optional brand image (keep relative path correct for your project)
    try {
      // require may fail in some bundler setups; guard it
      // eslint-disable-next-line global-require
      this.brandImage = require("../../../assets/brand_small.jpg");
    } catch (e) {
      this.brandImage = null;
    }

    this.addHeaderFooter();
  }

  /***********************
   * Page / header utils
   ***********************/
  addHeaderFooter() {
    const d = this.doc;

    // Header (fill)
    d.setFillColor(this.primary);
    d.rect(0, 0, this.pageWidth, this.headerHeight, "F");

    // Brand image + title in header
    try {
      if (this.brandImage) {
        d.addImage(this.brandImage, "PNG", this.margin, 2, 22, 10);
      }
      d.setFont("helvetica", "bold");
      d.setFontSize(14);
      d.setTextColor("#FFFFFF");
      d.text(this.locale === 'de' ? "Digitale Produktpässe" : "Digital Product Passports", this.margin + 32, 9);
    } catch (e) {
      // fallback text only
      d.setFont("helvetica", "bold");
      d.setFontSize(12);
      d.setTextColor("#FFFFFF");
      d.text("Brand Logo", this.margin, 10);
    }

    // Footer page number
    d.setFont("helvetica", "normal");
    d.setFontSize(8);
    d.setTextColor(this.text);
    d.text(
      this.locale === 'de' ? 
      `Seite ${d.internal.getCurrentPageInfo().pageNumber}` : 
      `Page ${d.internal.getCurrentPageInfo().pageNumber}`
      ,
      this.pageWidth - this.margin,
      this.pageHeight - 5,
      { align: "right" }
    );

    this.y = this.margin + this.headerHeight;
  }

  checkSpace(requiredHeight) {
    if (this.y + requiredHeight > this.pageHeight - this.margin) {
      this.doc.addPage();
      this.addHeaderFooter();
    }
  }

  addSpacing(px = 5) {
    this.checkSpace(px);
    this.y += px;
  }

  /***********************
   * Titles / sections
   ***********************/
  title(text) {
    this.checkSpace(12);
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(18);
    this.doc.setTextColor(this.primary);
    this.doc.text(text, this.margin, this.y);
    this.y += 12;
  }

  sectionHeader(text) {
    this.checkSpace(this.lineHeight + 6);

    this.doc.setFillColor(this.white);
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(14);
    this.doc.setTextColor(this.accent);

    this.doc.rect(
      this.margin,
      this.y - 5,
      this.pageWidth - 2 * this.margin,
      this.lineHeight + 4,
      "F"
    );

    this.doc.text(text, this.margin + 0, this.y);
    this.y += this.lineHeight + 2;
  }

  sectionHeaderSmall(text) {
      // Increase the top margin (e.g., add 3 more units to the top margin)
      const additionalTopMargin = 1;  // You can adjust this value for more or less spacing

      // Ensure enough space before rendering the header
      this.checkSpace(this.lineHeight + 6 + additionalTopMargin);

      this.doc.setFillColor(this.white);
      this.doc.setFont("helvetica", "bold");
      this.doc.setFontSize(12);
      this.doc.setTextColor(this.text);

      // Adjust the Y position to add the extra margin
      this.doc.rect(
        this.margin,
        this.y - 5 + additionalTopMargin,  // Adjust the starting Y position
        this.pageWidth - 2 * this.margin,
        this.lineHeight + 4,
        "F"
      );

      this.doc.text(text, this.margin + 0, this.y + additionalTopMargin); // Adjust the text position
      this.y += this.lineHeight + additionalTopMargin;  // Adjust the Y position after the header
  }


  keyValueRow(label, value, indent = 1) {
    const x = this.margin + (indent - 1) * 5;
    const labelStr = String(label ?? "");
    const valueStr = String(value ?? "");

    const rightX = this.pageWidth - this.margin;

    // Determine max width for the value column
    const maxValueWidth = rightX - x - 100; // 40mm reserved for label; adjust as needed

    // Split into wrapped lines
    const valueLines = this.doc.splitTextToSize(valueStr, maxValueWidth);

    // The row height is the max of 1 label line vs N value lines
    const rowHeight = valueLines.length * (this.lineHeight);

    this.checkSpace(rowHeight);

    // Label: always single-line
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(10);
    this.doc.setTextColor(this.text);
    this.doc.text(labelStr, x, this.y);

    // Value: right-aligned multiline
    this.doc.setTextColor(this.primary);
    valueLines.forEach((line, i) => {
      this.doc.text(line, rightX, this.y + i * (this.lineHeight - 3), { align: "right" });
    });

    // Move Y down by the total height
    this.y += rowHeight;
  }


  /**
 * Flexible multi-column layout engine.
 *
 * Usage:
 *   builder.columns(2, ({ col }) => {
 *      col(0).run(() => renderLeftStuff());
 *      col(1).run(() => renderRightStuff());
 *   });
 *
 * Automatically:
 *  - Computes column widths
 *  - Saves/restores builder.y per column
 *  - Tracks tallest column and updates builder.y afterward
 */
  columns(columnCount, renderFn, opts = {}) {
    const gap = opts.gap || 5;

    const fullWidth = this.pageWidth - 2 * this.margin;
    const columnWidth = (fullWidth - gap * (columnCount - 1)) / columnCount;

    const startY = this.y;

    const colYs = Array(columnCount).fill(startY);

    // SAVE ORIGINAL METHODS ONCE — BEFORE ANY OVERRIDE
    const original = {
      text: this.text,
      textBlock: this.textBlock,
      keyValueRow: this.keyValueRow,
      image: this.image,
    };

    const context = {
      col: (i) => ({
        run: (fn) => {
          const colX = this.margin + i * (columnWidth + gap);

          const prevY = this.y;
          this.y = colYs[i];

          // OVERRIDE METHODS (but using original references)
          this.text = (text, indent = 1) => {
            const x = colX + (indent - 1) * 5;
            const maxW = columnWidth - (indent - 1) * 5;
            const lines = this.doc.splitTextToSize(String(text), maxW);
            this.checkSpace(lines.length * this.lineHeight);
            this.doc.setTextColor("#003E81");
            this.doc.text(lines, x, this.y);
            this.y += lines.length * this.lineHeight;
          };

          this.textBlock = this.text;

          this.keyValueRow = (label, value, indent = 1) => {
            const x = colX + (indent - 1) * 5;
            const rightX = colX + columnWidth;

            // *** FIX: font, size, colors ***
            this.doc.setFont("helvetica", "normal");
            this.doc.setFontSize(10);

            // label = black
            this.doc.setTextColor("#000000");
            this.doc.text(String(label), x, this.y);

            // value = black (or use this.primary if desired)
            this.doc.setTextColor("#003E81");
            this.doc.text(String(value), rightX, this.y, { align: "right" });

            this.y += this.lineHeight;
          };


          this.image = (img, opts = {}) => {
            // Force correct column X but do NOT force width or height
            const adjustedOpts = {
              ...opts,
              x: colX + 5,
              // y should stay auto (builder.y) unless caller supplied it.
            };

            // Use original image renderer
            original.image.call(this, img, adjustedOpts);
          };


          // EXECUTE USER BLOCK
          fn();

          // Store updated column height
          colYs[i] = this.y;

          // RESTORE original functions + Y
          this.text = original.text;
          this.textBlock = original.textBlock;
          this.keyValueRow = original.keyValueRow;
          this.image = original.image;

          this.y = prevY;
        },
      }),
    };

    // Run user callback (sets up columns)
    renderFn(context);

    // Set global Y to tallest column
    this.y = Math.max(...colYs) + 10;
  }

  addDividerLine() {
    // Define the Y position where the divider line should be placed
    const dividerY = this.y; // You can adjust this to add more or less space above the line
    
    // Set the color of the divider (e.g., light gray or a custom color)
    this.doc.setDrawColor(230, 230, 230);  // Light gray color (adjust RGB as needed)
    
    // Draw the line across the page width
    this.doc.line(this.margin, dividerY, this.pageWidth - this.margin, dividerY);  // (x1, y1, x2, y2)
    
    // Optionally, add some space after the line to move down
    this.y = dividerY + 8;  // Adjust Y position after the line
  }


  /***********************
   * Text helpers
   ***********************/
  // Single-line convenience wrapper
  text(text, indent = 1) {
    if (text == null) return;
    const single = String(text);
    this.checkSpace(this.lineHeight);
    const x = this.margin + (indent - 1) * 5;

    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(11);
    this.doc.setTextColor(this.text);

    // Use splitTextToSize for safety if it's long
    const maxWidth = this.pageWidth - 2 * this.margin - (indent - 1) * 5;
    const lines = this.doc.splitTextToSize(single, maxWidth);
    this.doc.text(lines, x, this.y);
    this.y += lines.length * this.lineHeight;
  }

  // Multi-line block with wrapping
  textBlock(text, indent = 1) {
    if (text == null) return;
    const x = this.margin + (indent - 1) * 5;
    const width = this.pageWidth - 2 * this.margin - (indent - 1) * 5;

    const lines = this.doc.splitTextToSize(String(text), width);
    this.checkSpace(lines.length * this.lineHeight);
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(11);
    this.doc.setTextColor(this.text);
    this.doc.text(lines, x, this.y);
    this.y += lines.length * this.lineHeight;
  }

  newPageIfNotFirst(i) {
    if (i > 0) {
      this.doc.addPage();
      this.addHeaderFooter();
    }
  }

  /***********************
   * Image helper
   *
   * imageData: dataURL or base64 string with mime prefix (data:<type>;base64,...)
   * opts: { width, height, x, y } - dimensions in mm (jsPDF unit)
   ***********************/
  image(imageData, opts = {}) {
    if (!imageData) return;

    // Accept either a full data URL or a base64 string with MIME.
    let dataUrl = imageData;
    // If caller accidentally passed a Blob, convert would be needed; we expect data URL here.
    if (imageData instanceof Blob) {
      // NOTE: synchronous Blob->DataURL is not possible here; caller should pass data URL.
      console.warn("PdfBuilder.image: received Blob — convert to data URL before calling.");
      return;
    }

    const format = this._detectImageFormat(dataUrl) || "PNG";

    // Default size: width 60mm, height auto
    const maxWidth = opts.width || 60;
    let width = opts.width;
    let height = opts.height;

    // If only width provided, determine height from image natural ratio:
    // We don't have easy access to image intrinsic width/height without loading it.
    // So, as pragmatic approach, if height not provided, use width and assume 4:3 ratio.
    if (!height) {
      // Conservative default aspect ratio 4:3 -> height = width * 3/4
      height = Math.round(maxWidth * 0.75 * 100) / 100;
    }

    // If caller provided both, use them.

    // Position:
    const x = typeof opts.x === "number" ? opts.x : this.margin;
    const y = typeof opts.y === "number" ? opts.y : this.y;

    // Ensure space for image
    this.checkSpace(height + 4);

    try {
      this.doc.addImage(dataUrl, format, x, y, width, height);
      // update y if image was drawn at current y
      if (y === this.y) {
        this.y += height + 4; // add small gap after image
      } else {
        // if custom y provided, don't alter current y
      }
    } catch (err) {
      console.error("PdfBuilder.image: failed to add image:", err);
      // fallback text
      this.text("Image not available");
    }
  }

  // Very small helper to check prefix and return jsPDF format
  _detectImageFormat(dataUrl) {
    if (!dataUrl || typeof dataUrl !== "string") return null;
    const prefix = dataUrl.slice(0, 30).toLowerCase();
    if (prefix.includes("data:image/jpeg") || prefix.includes("data:image/jpg")) return "JPEG";
    if (prefix.includes("data:image/png")) return "PNG";
    if (prefix.includes("data:image/webp")) return "WEBP";
    if (prefix.includes("data:image/svg+xml")) return "SVG";
    return null;
  }
}
